import React, { forwardRef, type ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@tectonics/utils/utils';

const inputVariants = cva(
  'outline-none ring-inset',
  {
    variants: {
      variant: {
        default: 'border-input-border text-input-fg placeholder:text-input-placeholder',
        ghost: 'w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-neutral-700 placeholder:text-neutral-400 focus:ring-0 disabled:placeholder:text-neutral-400 invalid:placeholder:text-error-dark invalid:focus:placeholder:text-neutral-400',
      },
      lookAndFeel: {
        default: '',
        volcanes: '',
        shopify: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        lookAndFeel: 'default',
        class: 'max-h-10 py-2.5 px-3 rounded-base gap-2 self-stretch border-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-neutral-900 disabled:placeholder:text-neutral-400 disabled:bg-neutral-200 invalid:border-stroke-error invalid:placeholder:text-error-dark invalid:focus:placeholder:text-neutral-400',
      },
      {
        variant: 'default',
        lookAndFeel: 'volcanes',
        class: 'max-h-12.5 border px-3 py-4 rounded-sm focus:border-transparent focus:ring-2 focus:ring-wl-highlight invalid:bg-volcanes-error-bg invalid:ring-[1.5px] invalid:ring-volcanes-error-default invalid:focus:bg-transparent aria-invalid:bg-volcanes-error-bg aria-invalid:ring-[1.5px] aria-invalid:ring-volcanes-error-default aria-invalid:focus:bg-transparent data-[error=error]:bg-volcanes-error-bg data-[error=error]:ring-[1.5px] data-[error=error]:ring-volcanes-error-default data-[error=error]:focus:bg-transparent',
      },
      {
        variant: 'default',
        lookAndFeel: 'shopify',
        class: 'h-[48px] border p-[14px] rounded-[3px] bg-white focus:border-input-border-focus aria-invalid:border-input-border-error data-[error=error]:border-input-border-error',
      },
    ],
    defaultVariants: {
      variant: 'default',
      lookAndFeel: 'default',
    },
  }
);

const Input = forwardRef<HTMLInputElement, ComponentProps<'input'> & VariantProps<typeof inputVariants>>(
  ({ className, type, variant, lookAndFeel = 'default', ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, lookAndFeel }), className)}
        ref={ref}
        {...props}
        data-look-and-feel={lookAndFeel}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
